import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

const SURROGACY_IMG = 'https://readdy.ai/api/search-image?query=professional%20medical%20fertility%20clinic%20surrogacy%20consultation%2C%20warm%20and%20caring%20doctor%20with%20couple%2C%20clean%20white%20medical%20room%2C%20soft%20lighting%2C%20modern%20medical%20equipment%2C%20hopeful%20atmosphere%2C%20gentle%20tones%20champagne%20gold%20and%20white&width=600&height=400&seq=svc-surrogacy-001&orientation=landscape';
const IVF_IMG = 'https://readdy.ai/api/search-image?query=IVF%20laboratory%20science%20embryo%20microscope%2C%20clean%20modern%20medical%20research%20lab%2C%20blue%20and%20white%20tones%2C%20professional%20fertility%20treatment%2C%20high-tech%20medical%20equipment%2C%20pristine%20environment%2C%20scientific%20precision&width=600&height=400&seq=svc-ivf-001&orientation=landscape';
const EGG_IMG = 'https://readdy.ai/api/search-image?query=egg%20donation%20medical%20consultation%20women%20healthcare%2C%20warm%20professional%20clinic%2C%20champagne%20gold%20soft%20tones%2C%20caring%20medical%20team%2C%20modern%20fertility%20clinic%2C%20hopeful%20and%20professional%20atmosphere&width=600&height=400&seq=svc-egg-001&orientation=landscape';

const FALLBACK_SERVICES = [
  {
    key: 'ivf',
    titleKey: 'service_ivf',
    descKey: 'service_ivf_desc',
    href: '/ivf',
    img: IVF_IMG,
    icon: 'ri-dna-line',
    color: 'from-[#2a5a8c] to-[#1a7a8c]',
  },
  {
    key: 'surrogacy',
    titleKey: 'service_surrogacy',
    descKey: 'service_surrogacy_desc',
    href: '/surrogacy',
    img: SURROGACY_IMG,
    icon: 'ri-heart-pulse-line',
    color: 'from-[#1a3a5c] to-[#2a5a8c]',
  },
  {
    key: 'egg',
    titleKey: 'service_egg',
    descKey: 'service_egg_desc',
    href: '/fertility',
    img: EGG_IMG,
    icon: 'ri-shield-check-line',
    color: 'from-[#c9a96e] to-[#b8944f]',
  },
];

export default function ServicesSection() {
  const { t } = useTranslation();
  const [services, setServices] = useState(FALLBACK_SERVICES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchServices = async () => {
      setLoading(true);
      setError('');
      try {
        const { data, error: supaErr } = await supabase
          .from('site_settings')
          .select('key, value')
          .in('key', ['service_ivf_title', 'service_ivf_desc', 'service_surrogacy_title', 'service_surrogacy_desc', 'service_egg_title', 'service_egg_desc']);
        if (supaErr) throw supaErr;
        // Always use fallback with proper i18n keys - dynamic settings can override if needed
        setServices(FALLBACK_SERVICES);
      } catch (err: any) {
        // Fallback already set, only show error if network issue
        if (err?.message) {
          setError(err.message);
        }
        setServices(FALLBACK_SERVICES);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-[#f8f5f0]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('services_label')}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c]">{t('services_title')}</h2>
          </div>
          <p className="text-gray-500 text-sm md:text-base max-w-md text-right">{t('services_subtitle')}</p>
        </div>

        {/* Loading state */}
        {loading && services.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-loader-4-line text-3xl text-[#c9a96e] animate-spin block mb-3"></i>
            <p className="text-gray-400 text-sm">{t('loading')}</p>
          </div>
        ) : error ? (
          /* Error state */
          <div className="text-center py-16">
            <i className="ri-error-warning-line text-4xl text-red-300 mb-3 block"></i>
            <p className="text-red-500 text-sm mb-4">{t('faq_load_error')}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 bg-[#1a3a5c] text-white text-sm font-medium px-6 py-2.5 rounded-full cursor-pointer hover:bg-[#0d2137] transition-colors whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>{t('retry')}
            </button>
          </div>
        ) : (
          /* Service cards grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc) => (
              <Link
                to={svc.href}
                key={svc.key}
                className="group relative bg-white rounded-2xl overflow-hidden cursor-pointer hover:-translate-y-1 transition-all duration-300"
              >
                {/* Image */}
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={svc.img}
                    alt={t(svc.titleKey || '')}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Icon badge */}
                <div className={`absolute top-4 left-4 w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${svc.color} text-white`}>
                  <i className={`${svc.icon} text-lg`}></i>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-bold text-[#1a3a5c] text-base mb-2">{t(svc.titleKey || '')}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{t(svc.descKey || '')}</p>
                  <div className="flex items-center gap-1 text-[#c9a96e] text-sm font-medium group-hover:gap-2 transition-all">
                    {t('service_more')}
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}